import type { UserDetail } from '@/generated/http-clients/v3ApiSchemas'

/**
 * How a user entered the HiveMQ Platform rollout — the question support is
 * actually asking when they open this page.
 *
 * Derived from the two `bucketed_to_hivemq_app_*` flags the Auth0 post-login
 * Action persists in `user_metadata`. The Action writes each flag exactly once,
 * at the moment it buckets the user, and never writes `false` — the Console
 * branches write nothing at all. So a flag is either `true` or absent, and
 * which one is present tells you the path taken:
 *
 *   - `post_signup` — written at first login (`logins_count === 1`), either
 *     because signup originated on the HiveMQ App Auth0 client or because the
 *     `hivemq-app-post-signup-migration` dial bucketed them. These users
 *     registered *after* the rollout began.
 *   - `post_login`  — written past first login by the
 *     `hivemq-app-post-login-migration` dial. These users registered *before*
 *     the rollout and were swept forward later.
 *   - neither, but `target_app_url` is set — nothing in the rollout Action
 *     wrote this, so it came from the user themselves: Cloud Console's "Try
 *     the new app" banner patches `target_app_url` through
 *     `PATCH /users/me/metadata` without touching either flag.
 *   - neither, and no `target_app_url` — never bucketed. Re-evaluated against
 *     the dial on every login; *not* the same as "stays on Cloud Console".
 *
 * Mirrors `deriveUserMigrationType` in the HiveMQ App SPA
 * (`app/features/rollout/utils/claims.ts`), which splits the same three
 * `post_signup | post_login | by_choice` cases off the matching ID-token
 * claims. Kept in the same order so the two never disagree.
 */
export type PlatformMigrationCohort =
  | 'registeredInPlatform'
  | 'migrated'
  | 'optedIn'
  | 'notMigrated'

export type PlatformMigration = {
  cohort: PlatformMigrationCohort
  /**
   * Which app the user lands in today, from the persisted `target_app_url`.
   *
   * Independent of the cohort: the Action only ever writes `target_app_url` to
   * the HiveMQ App URL, so a bucketed user reporting `CLOUD_CONSOLE` switched
   * back of their own accord.
   *
   * Not live routing truth — the `hivemq-app-kill-switch` and
   * `force-all-users-to-hive-mq-app` LaunchDarkly flags override routing as
   * claim-only and deliberately leave `user_metadata` untouched.
   */
  usesPlatform: boolean
}

export function derivePlatformMigration(user: UserDetail): PlatformMigration {
  const usesPlatform = user.bucketedApp === 'HIVEMQ_APP'

  // Order matters, and matches the SPA: a user can in principle carry both
  // flags (the Action never clears one), and post_signup is the stronger
  // statement about how they arrived.
  if (user.bucketedToHivemqAppPostSignup === true) {
    return { cohort: 'registeredInPlatform', usesPlatform }
  }
  if (user.bucketedToHivemqAppPostLogin === true) {
    return { cohort: 'migrated', usesPlatform }
  }
  if (user.bucketedApp !== 'NOT_BUCKETED') {
    return { cohort: 'optedIn', usesPlatform }
  }
  return { cohort: 'notMigrated', usesPlatform }
}
